import { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from 'types';
import { useTheme } from 'utils/context/ThemeProvider';
import { getDomainConfig } from 'utils/whiteLabel';
import { getActiveToken } from 'utils/tabAuth';
import {
  CHANGE_EVENT,
  DEFAULT_HEADER_SUBTITLE,
  belongsToEmployee,
  ensureActiveTestUser,
  getActiveTestUser,
  resolveTesterIdentity,
  type ActiveTestUser,
} from './helpers/testWidgetUsers';

const STORAGE_KEY = 'heltar:test-widget';
export { CHANGE_EVENT };

export const isTestWidgetOn = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

export const setTestWidgetOn = (on: boolean): void => {
  try {
    if (on) localStorage.setItem(STORAGE_KEY, '1');
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

const CONFIG_KEY = 'heltar:test-widget-config';

export const getTestWidgetConfig = (): Record<string, unknown> => {
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    return raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
  } catch {
    return {};
  }
};

export const setTestWidgetConfig = (
  config: Record<string, unknown> | null,
): void => {
  try {
    if (config && Object.keys(config).length)
      localStorage.setItem(CONFIG_KEY, JSON.stringify(config));
    else localStorage.removeItem(CONFIG_KEY);
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

type WidgetWindow = Window & {
  HeltarChat?: {
    initBubble: (props: unknown) => void;
    unmount: () => void;
    setMode?: (mode: 'light' | 'dark' | 'system') => void;
  };
};

const ensureWidgetScript = (): Promise<void> =>
  new Promise((resolve, reject) => {
    const win = window as WidgetWindow;
    if (win.HeltarChat) return resolve();
    const existing = document.querySelector(
      'script[data-heltarchat-test-widget]',
    );
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load widget bundle')),
      );
      return;
    }
    const script = document.createElement('script');
    script.src = `/web-widget.js?t=${Date.now()}`;
    script.defer = true;
    script.setAttribute('data-heltarchat-test-widget', 'true');
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error('Failed to load /web-widget.js from this dashboard'));
    document.body.appendChild(script);
  });

export const TestWidgetHost = memo(function TestWidgetHost() {
  const businessId = useSelector((s: RootState) => s.authPage?.business?.id);
  const employeeId = useSelector(
    (s: RootState) => s.authPage?.currentEmployee?.id,
  );
  const employeeName = useSelector(
    (s: RootState) => s.authPage?.currentEmployee?.name,
  );
  const { theme } = useTheme();
  const [enabled, setEnabled] = useState<boolean>(isTestWidgetOn());
  const [config, setConfig] = useState<Record<string, unknown>>(
    getTestWidgetConfig(),
  );
  const [activeUser, setActiveUser] = useState<ActiveTestUser | null>(
    getActiveTestUser(),
  );

  useEffect(() => {
    const sync = (): void => {
      setEnabled(isTestWidgetOn());
      setConfig(getTestWidgetConfig());
      setActiveUser(getActiveTestUser());
    };
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const active = enabled && !!businessId && !!employeeId;

  useEffect(() => {
    if (!active || !businessId || !employeeId) return;
    const cur = getActiveTestUser();
    if (cur && belongsToEmployee(cur.id, employeeId)) return;
    void ensureActiveTestUser(businessId, employeeId);
  }, [active, businessId, employeeId]);

  const configKey = JSON.stringify(config);

  useEffect(() => {
    if (
      !active ||
      !businessId ||
      !employeeId ||
      !activeUser ||
      !belongsToEmployee(activeUser.id, employeeId)
    )
      return;
    let cancelled = false;
    void ensureWidgetScript()
      .then(() => {
        if (cancelled) return;
        const win = window as WidgetWindow;
        const hash = getActiveToken();
        const cfgTheme =
          config.theme && typeof config.theme === 'object'
            ? (config.theme as Record<string, unknown>)
            : {};
        const { visitorName, headerTitle } = resolveTesterIdentity(
          activeUser.name,
          employeeName,
        );
        win.HeltarChat?.initBubble({
          businessId,
          apiHost: getDomainConfig().apiUrl,
          mode: typeof config.mode === 'string' ? config.mode : theme,
          ...(typeof config.dynamicPrompt === 'string'
            ? { dynamicPrompt: config.dynamicPrompt }
            : {}),
          visitor: {
            id: activeUser.id,
            name: visitorName,
            ...(hash ? { hash } : {}),
          },
          theme: {
            headerTitle,
            headerSubtitle: DEFAULT_HEADER_SUBTITLE,
            ...cfgTheme,
          },
        });
      })
      .catch(() => {
        /* ignore */
      });
    return () => {
      cancelled = true;
      const win = window as WidgetWindow;
      try {
        win.HeltarChat?.unmount();
      } catch {
        /* ignore */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    active,
    businessId,
    employeeId,
    employeeName,
    configKey,
    activeUser?.id,
    activeUser?.name,
  ]);

  useEffect(() => {
    if (!active) return;
    (window as WidgetWindow).HeltarChat?.setMode?.(theme);
  }, [theme, active]);

  return null;
});
